import { Spacing } from '@persona-id/design-tokens';
import { SubscribeIcon } from '@persona-id/design-tokens/icons';
import { useEffect } from 'react';
import styled from 'styled-components';

import { SearchListsSelectTable } from 'dashboard/forms/FormReportTemplate/SearchListsSelectTable';
import { ExpandableContent } from 'dashboard/forms/FormReportTemplate/WatchlistListsSelect/ExpandableContent';
import {
  WatchlistSourceListDisplay,
  WatchlistSubscriptionContainer,
  WatchlistSubscriptionDisplay,
  WatchlistSubscriptionName,
} from 'dashboard/forms/FormReportTemplate/WatchlistListsSelect/WatchlistListsOptionDisplay';
import { WatchlistListOption } from 'dashboard/forms/FormReportTemplate/WatchlistListsSelect/WatchlistListsSelect';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import TabBar, { Tab } from 'design-system/components/TabBar';
import { Paragraph } from 'design-system/components/Text';
import useToggle from 'lib/hooks/useToggle';

const getSubscriptionsFromList = (
  list: string,
  subscriptions: string[],
  subscriptionOptions: WatchlistListOption[],
) => {
  return subscriptionOptions.reduce((subs, option) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    if (subscriptions.includes(option.value) && option.lists.includes(list)) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      subs.push(option.name);
    }
    return subs;
  }, []);
};

interface WatchlistListsDisplayProps {
  subscribedToDefault: boolean;
  subscriptions: string[];
  lists: string[];
  listOptions: WatchlistListOption[];
  subscriptionOptions: WatchlistListOption[];
  disabled: boolean;
}

export const WatchlistListsDisplay = ({
  subscribedToDefault,
  subscriptions,
  lists,
  listOptions,
  subscriptionOptions,
}: WatchlistListsDisplayProps) => {
  const [subscriptionTab, { enable: showSubscriptionTab, disable: hideSubscriptionTab }] =
    useToggle(!(lists?.length > 0 && subscriptions?.length <= 0));

  const listsFromSubscriptions = new Set(
    subscribedToDefault
      ? [...listOptions.map((list) => list.value)]
      : subscriptions.reduce((acc, subscriptionSlug) => {
          const subscription = subscriptionOptions.find(
            (option) => option.value === subscriptionSlug,
          );
          if (subscription) {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            acc = [...subscription.lists, ...acc];
          }
          return acc;
        }, []),
  );
  const allLists = Array.from(new Set([...lists, ...Array.from(listsFromSubscriptions)]));

  useEffect(() => {
    if (lists?.length > 0 && subscriptions?.length <= 0) {
      hideSubscriptionTab();
    } else {
      showSubscriptionTab();
    }
  }, [lists, subscriptions, hideSubscriptionTab, showSubscriptionTab]);

  return (
    <>
      <TabBar>
        <Tab
          type='button'
          id='watchlist-lists-select-modal__subscription-tab'
          selected={subscriptionTab}
          badge={<StyledSticker variant='grey'>{subscriptions.length}</StyledSticker>}
          onClick={() => showSubscriptionTab()}
        >
          Category Subscriptions
        </Tab>
        <Tab
          type='button'
          id='watchlist-lists-select-modal__source-lists-tab'
          selected={!subscriptionTab}
          badge={<StyledSticker variant='grey'>{allLists.length}</StyledSticker>}
          onClick={() => hideSubscriptionTab()}
        >
          Individual Lists
        </Tab>
      </TabBar>
      <Spacer height={1} />
      {subscriptionTab ? (
        <SearchListsSelectTable
          key='subscriptions'
          options={subscriptionOptions}
          values={subscriptions}
          valueDisplayName='Category subscription name'
          categoryDisplayName='Type'
          hideFilters={true}
          viewMode={true}
          selectAllDisplay={
            <WatchlistSubscriptionContainer>
              <WatchlistSubscriptionName>
                <SubscribeIcon />
                <span>All subscriptions ({`${subscriptionOptions.length}`})</span>
              </WatchlistSubscriptionName>
              <ExpandableContent id='all-lists' title={`${listOptions.length} lists`}>
                <ul>
                  {listOptions.map((option) => (
                    <li key={option.value}>
                      <Paragraph size='sm' color='secondary'>
                        {option.name}
                      </Paragraph>
                    </li>
                  ))}
                </ul>
              </ExpandableContent>
            </WatchlistSubscriptionContainer>
          }
          optionDisplay={(option: WatchlistListOption) => (
            <WatchlistSubscriptionDisplay option={option} listOptions={listOptions} />
          )}
        />
      ) : (
        <SearchListsSelectTable
          key='lists'
          options={listOptions}
          values={allLists}
          valueDisplayName='Individual list name'
          categoryDisplayName='Type'
          hideFilters={true}
          viewMode={true}
          optionDisplay={(option: WatchlistListOption) => (
            <WatchlistSourceListDisplay
              option={option}
              subscriptions={getSubscriptionsFromList(
                option.value,
                subscriptions,
                subscriptionOptions,
              )}
            />
          )}
        />
      )}
    </>
  );
};

const StyledSticker = styled(Sticker)`
  margin-left: ${Spacing[2]};
`;
